import { watchDebounced } from '@vueuse/core';
import { FuseOptions, useFuse } from '@vueuse/integrations/useFuse.mjs';
import {
  computed, ComputedRef, MaybeRefOrGetter, Ref, ref,
} from 'vue';

import { i18n } from '@/i18n';

export interface DropdownSearcher<T> {
  (records: Ref<T[]>, labelKey: keyof T | undefined): {
    filtered: ComputedRef<T[]> | Ref<T[]>;
    searchString: Ref<string>;
    isLoading: Ref<boolean>;
  };
}

export function getFuseDropdownSearcher<T>(fuseOptions: FuseOptions<T> = { threshold: 0.1 }): DropdownSearcher<T> {
  return (records, labelKey) => {
    const searchString = ref('');

    const defaultKeys = [{
      name: 'label',
      getFn: (item: any) => {
        if (!item[labelKey || 'label']) return '';
        if (!i18n.global.te(item[labelKey || 'label'])) return item[labelKey || 'label'];
        return i18n.global.t(item[labelKey || 'label']);
      },
    }];

    const { results } = useFuse<T>(searchString, records, {
      fuseOptions: {
        ...fuseOptions,
        keys: fuseOptions.keys ?? defaultKeys,
      },
      matchAllWhenSearchEmpty: true,
    });

    return {
      filtered: computed<T[]>(() => results.value.map((result) => result.item)),
      searchString,
      isLoading: ref(false),
    };
  };
}

interface GetApiDropdownSearcherOptions {
  debounce: MaybeRefOrGetter<number>;
  maxWait?: MaybeRefOrGetter<number>;
}
export function getApiDropdownSearcher<T>(
  onChangeFilter: (searchString: string) => Promise<void>,
  options: GetApiDropdownSearcherOptions = { debounce: 500, maxWait: 1000 },
): DropdownSearcher<T> {
  return (records) => {
    const searchString = ref('');
    const isLoading = ref(false);

    watchDebounced(searchString, async () => {
      isLoading.value = true;
      await onChangeFilter(searchString.value);
      isLoading.value = false;
    }, options);

    return {
      filtered: records,
      searchString,
      isLoading,
    };
  };
}
